import { type PrismicDesktopCategoryItem, newHeaderEvent } from "@jog/shared"
import {
    type AnchorHTMLAttributes,
    type CSSProperties,
    type FC,
    forwardRef,
    useCallback,
    useRef,
    useState,
} from "react"

import { usePrefetch } from "../../prefetch"
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
    NavigationMenuViewport,
    cn,
} from "../../ui"
import { Container } from "../Container/Container"
import { Link } from "../Link/Link"

export interface NavCategoryStyle extends CSSProperties {
    "--text-color": string
    "--bg-color"?: string
}
export const NavItemRichContent = ({ htmlStr }: { htmlStr: string }) => (
    <div className="mb-0" dangerouslySetInnerHTML={{ __html: htmlStr }} />
)

export const MaybeLink = forwardRef<
    HTMLAnchorElement,
    Pick<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "children" | "className" | "style"> & { gtm?: boolean }
>(
    (
        {
            children,
            href,
            gtm,
            className,
            ...props
        }: Pick<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "children" | "className" | "style"> & {
            gtm?: boolean
        },
        ref
    ) => {
        const { menu } = newHeaderEvent()
        if (!href)
            return (
                <div className={cn(className)} {...props}>
                    {children}
                </div>
            )
        return (
            <NavigationMenuLink asChild>
                <Link
                    href={href}
                    ref={ref}
                    onClick={() => gtm && menu()}
                    className={cn("group/link", className)}
                    {...props}>
                    {children}
                </Link>
            </NavigationMenuLink>
        )
    }
)
MaybeLink.displayName = "MaybeLink"

export const CategoryTag = ({
    label,
    color,
    backgroundColor,
    className,
}: {
    label: string
    color: string
    backgroundColor: string
    className?: string
}) => {
    if (!label) return <></>
    const style: NavCategoryStyle = { "--text-color": color || "white", "--bg-color": backgroundColor || "black" }
    return (
        <span
            className={cn(
                "ml-2 flex items-center justify-center bg-[var(--bg-color)] px-1 text-center text-0.85 text-[var(--text-color)]",
                className
            )}
            style={style}>
            {label}
        </span>
    )
}

export const CategoryTrigger = ({
    navItem,
    className,
    gtm,
}: {
    navItem: PrismicDesktopCategoryItem
    className?: string
    gtm?: boolean
}) => {
    const style: NavCategoryStyle = { "--text-color": navItem.categoryTextColor || "inherit" }
    return (
        <NavigationMenuTrigger disabled={!navItem.subCategoryItems.length} asChild>
            <MaybeLink
                gtm={gtm}
                className={cn(
                    "relative flex h-10 cursor-pointer items-center justify-center text-center uppercase text-gray-base",
                    className
                )}
                style={style}
                href={navItem.categoryUrl}>
                <div className="px-4 text-[var(--text-color)]">
                    <span className="absolute left-1/2 top-full z-20 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-b-0 border-r-0 border-gray-light bg-white group-data-[state=closed]:hidden" />
                    <NavItemRichContent htmlStr={navItem.categoryName} />
                </div>
            </MaybeLink>
        </NavigationMenuTrigger>
    )
}

export const DesktopNavMenu = ({
    Trigger,
    MenuContent,
}: {
    Trigger: FC<{ navItem: PrismicDesktopCategoryItem }>
    MenuContent: FC<{ navItem: PrismicDesktopCategoryItem }>
}) => {
    const { data: layout } = usePrefetch("layout")
    const delay = Math.max(layout?.layout?.navigationMenuHoverDelay || 400, 0)
    const [activeValue, setActiveValue] = useState<string>("")
    const timer = useRef<ReturnType<typeof setTimeout>>()
    const onValueChange = useCallback(
        (v?: string) => {
            if (v) {
                setActiveValue(v)
            } else {
                if (timer.current) {
                    clearTimeout(timer.current)
                }
                timer.current = setTimeout(
                    () => {
                        setActiveValue("")
                    },
                    Math.max(delay - 150, 0)
                )
            }
        },
        [delay]
    )
    return (
        <NavigationMenu
            delayDuration={delay}
            data-viewport
            className="w-full text-gray-base"
            onValueChange={onValueChange}
            value={activeValue}>
            <NavigationMenuList>
                {layout?.nav?.desktop.map((navItem) => (
                    <NavigationMenuItem key={navItem.id} value={navItem.id}>
                        <Trigger navItem={navItem} />
                        <NavigationMenuContent
                            forceMount
                            className="hidden data-[state=open]:block"
                            data-state={activeValue === navItem.id ? "open" : "closed"}>
                            <MenuContent navItem={navItem} />
                        </NavigationMenuContent>
                    </NavigationMenuItem>
                ))}
            </NavigationMenuList>
            <div className="shadow-dark-100 absolute top-full left-0 right-0 z-[1] w-full overflow-hidden border-t border-gray-light bg-white shadow-md">
                <Container>
                    <NavigationMenuViewport forceMount />
                </Container>
            </div>
        </NavigationMenu>
    )
}
